// RGB / WWH / WWRGB dimmable light. One VERTICAL 0-255 slider per channel, all
// always visible side by side (bottom = 0, top = 255). Publishes the comma-joined
// channel values on release; status is the same comma format (e.g. WWRGB
// 'w,ww,r,g,b'). Covers all three variants:
//   DeviceID 25, 90 → White / Warm (2ch)
//   DeviceID 24     → R / G / B (3ch)
//   DeviceID 23     → W / WW / R / G / B (5ch)
//
// Runs on core/hooks/useLightChannels, the phone's hook — so Reverse white /
// warm, On / Off / Night and Blink behave identically on both shells
// (ui/shared/controls/LightActions).

import React from 'react';
import { Box, Slider, Typography, Stack, Button } from '@mui/material';
import DeviceShell from '../../components/DeviceShell';
import { useApp } from '../../../../core/state/AppContext';
import { useLightChannels } from '../../../../core/hooks/useLightChannels';
import {
  RGB_PRESETS, WWH_PRESETS, WWRGB_PRESETS,
} from '../../../../core/devices/light';
import { LightActionRow, LightNotice, useLightMenu } from '../../../shared/controls/LightActions';

// Colour each channel's slider so it reads at a glance.
function channelColor(ch) {
  const c = String(ch).toLowerCase();
  if (c === 'r' || c.startsWith('red')) return '#e53935';
  if (c === 'g' || c.startsWith('green')) return '#43a047';
  if (c === 'b' || c.startsWith('blue')) return '#1e88e5';
  if (c === 'w' || c === 'white') return '#e0e0e0';
  if (c === 'ww' || c === 'wh' || c.startsWith('warm')) return '#ffb300';
  return '#90caf9';
}

// The same presets as the phone (core/devices/light), by channel count.
const presetsFor = (n) => (n === 2 ? WWH_PRESETS : n === 5 ? WWRGB_PRESETS : RGB_PRESETS); // eslint-disable-line no-nested-ternary

export default function LightScreen({ device, channels }) {
  const { userId } = useApp();
  const uno = device.UniqueNumber;
  const light = useLightChannels(uno, channels.length, userId);
  const { values, setValues, commit, sendCommand } = light;
  const menu = useLightMenu(device, light);

  const onChange = (i, v) => setValues((prev) => { const u = [...prev]; u[i] = v; return u; });
  const onCommit = (i, v) => { const u = [...values]; u[i] = v; commit(u); };

  return (
    <DeviceShell title={device.DeviceName || uno}>
      <Stack spacing={2} alignItems="center">
        {/* Vertical channel sliders — always visible, side by side. */}
        <Box sx={{ display: 'flex', gap: 2.5, justifyContent: 'center', alignItems: 'flex-end', py: 1 }}>
          {channels.map((ch, i) => (
            <Box key={ch} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
              <Typography variant="caption" color="text.primary" sx={{ minHeight: 16 }}>{values[i]}</Typography>
              <Slider
                orientation="vertical"
                min={0} max={255} value={values[i] || 0}
                disabled={light.blinking}
                onChange={(_, v) => onChange(i, v)}
                onChangeCommitted={(_, v) => onCommit(i, v)}
                sx={{
                  height: 200, color: channelColor(ch),
                  '& .MuiSlider-rail': { opacity: 0.25 },
                  '& .MuiSlider-thumb': { width: 18, height: 18 },
                }}
              />
              <Typography variant="caption" fontWeight={700} color="text.primary">{ch}</Typography>
            </Box>
          ))}
        </Box>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="center">
          {presetsFor(channels.length).map((p) => (
            <Button
              key={p.label} size="small" variant="outlined" disabled={light.blinking}
              onClick={() => sendCommand(p.command)}
            >
              {p.label.replace('\n', ' ').trim()}
            </Button>
          ))}
        </Stack>

        <LightActionRow light={light} size="small" />

        {/* The phone keeps these in ⋮; the page has room to show them. */}
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="center">
          {menu.items.filter((it) => it.key !== 'blink').map((it) => (
            <Button key={it.key} size="small" startIcon={it.icon} onClick={it.onClick} title={it.secondary}>
              {it.label}
            </Button>
          ))}
        </Stack>
      </Stack>
      <LightNotice notice={menu.notice} onClose={menu.clearNotice} />
    </DeviceShell>
  );
}
